import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import isEmpty from 'lodash.isempty';
import merge from 'lodash.merge';
import reduce from 'lodash.reduce';
import { NewRadioDropdown } from '@saucelabs/sl-component-form';
import { Row, Col } from '@saucelabs/sl-component-grid';
import { Loader } from '@saucelabs/sl-component-loader';

import Styles from './VirtualDeviceConfigurator.styles';
import {
  CONFIGURATION_DEVICE_TYPES,
  CONFIGURATION_TEST_TYPES,
  APP_STORAGE_KIND,
} from './../../constants';
import { actions as testConfigActions, selectors as testConfigurationSelectors } from '../../ducks/testConfiguration';
import VdcWarnings from '../../ducks/testConfiguration/components/VdcWarnings';
import StartVirtualSessionButton from '../StartVirtualSessionButton';

export const orderDevicesByManufacture = ({ devices, kind, testType }) => {
  const androidDevices = reduce(devices.android, (acc, value, key) => {
    const [manufacturer, ...deviceNameSplit] = key.split(' ');
    const deviceName = deviceNameSplit.join(' ');

    return merge(acc, { [manufacturer]: { [deviceName]: value } });
  }, {});

  if (testType === CONFIGURATION_TEST_TYPES.APP && kind) {
    if (kind === APP_STORAGE_KIND.ANDROID) {
      return androidDevices;
    }

    return { Apple: { ...devices.ios } };
  }

  return {
    ...androidDevices,
    Apple: { ...devices.ios },
  };
};

export const clearSelection = (selection, data) => {
  /* eslint-disable no-param-reassign */
  if (data.manufacturer) {
    selection.device = null;
    selection.os = null;
  }

  if (data.device) {
    selection.os = null;
  }
  /* eslint-enable no-param-reassign */
};

const VirtualDeviceConfigurator = () => {
  const {
    mobileAppConfiguration,
    testType,
    virtualDeviceOptions,
  } = useSelector(testConfigurationSelectors.virtualDeviceConfiguratorSelector);
  const kind = mobileAppConfiguration?.kind;

  const dispatch = useDispatch();

  const devicesByManufacturer = orderDevicesByManufacture({
    devices: virtualDeviceOptions,
    kind,
    testType,
  });
  const manufacturers = Object.keys(devicesByManufacturer).sort();

  const [state, setState] = useState({
    options: devicesByManufacturer,
    manufacturers,
    devices: [],
    oses: [],
    manufacturer: manufacturers[0],
    device: null,
    os: null,
  });

  const handleSelection = (data = {}) => {
    const { options, manufacturer, device, os } = state;

    const selection = {
      manufacturer,
      device,
      os,
      ...data,
    };

    clearSelection(selection, data);

    if (!selection.device) {
      // Sorting order of the devices is determined by the backend response
      const availableDevices = Object.keys(options[selection.manufacturer]);

      selection.devices = availableDevices;
      selection.device = selection.devices[0];
    }

    if (!selection.os) {
      const oses = Object.keys(options[selection.manufacturer][selection.device]);

      selection.oses = oses;
      selection.os = oses[0];
    }

    const configuration = options[selection.manufacturer][selection.device][selection.os] || {};

    const onChangeObj = Object.assign({}, {
      browser: configuration.browser,
      browserDisplay: configuration.device,
      browserVersionDisplay: '',
      device: configuration.device,
      os: configuration.platform,
      osDisplay: configuration.browser,
      osVersionDisplay: selection.os,
      res: null,
      version: configuration.version,
    });

    dispatch(testConfigActions.updateTestConfigValue({ virtualDeviceConfiguration: onChangeObj }));
    setState({ ...state, ...selection });
  };

  // componentDidMount
  useEffect(() => {
    dispatch(testConfigActions.updateTestConfigValue({
      deviceType: CONFIGURATION_DEVICE_TYPES.VIRTUAL_DEVICE,
    }));
    handleSelection({});
  // TODO: fix dependencies
  }, []);

  const renderDropdown = (config) => {
    const currentValue = state[config.keyName]; // eslint-disable-line
    const onChange = (newValue) => handleSelection({ [config.keyName]: newValue });
    const prepareOptions = (options) => options.map((item) => ({
      text: item,
      value: item,
    }));

    return (
      <Row key={config.keyName}>
        <Col xs>
          <NewRadioDropdown
            initialValue={currentValue}
            input={{
              value: currentValue,
              onChange,
            }}
            isInputForm
            label={config.label}
            options={prepareOptions(config.options)}
          />
        </Col>
      </Row>
    );
  };

  const { devices, oses } = state;
  const DROPDOWNS_CONFIG = [
    {
      label: 'Manufacturer',
      keyName: 'manufacturer',
      options: manufacturers,
    },
    {
      label: 'Device',
      keyName: 'device',
      options: devices,
    },
    {
      label: 'OS version',
      keyName: 'os',
      options: oses,
    },
  ];

  return (
    <Loader
      className={Styles.loader}
      fontSize={3}
      show={isEmpty(virtualDeviceOptions)}
    >
      <div className="pt-2 px-2">
        <VdcWarnings />
        <div className={Styles.default}>
          <Row>
            <Col xs>
              <div className={Styles['device-column']}>
                {DROPDOWNS_CONFIG.map(renderDropdown)}
                <StartVirtualSessionButton />
              </div>
            </Col>
          </Row>
        </div>
      </div>
    </Loader>
  );
};

export default VirtualDeviceConfigurator;
